import api from '@/services/api';

export interface ChatbotPickerOption {
  chatbot_id: string;
  name: string;
  system_prompt_tokens: number;
  /** TextureColor literal ('blue'|'green'|'red'|'yellow') or hex string for the avatar. */
  avatar_color: string | null;
  /** Texture variant index within the chosen color. */
  avatar_variant: number | null;
}

export interface ChatbotProfile {
  chatbot_id: string;
  name: string;
  system_prompt_tokens: number;
  /** Average tokens of user-only messages (sender_type='user'), strictly separate from assistant. */
  avg_user_msg_tokens: number;
  /** Average tokens of assistant-only responses (sender_type='assistant', completion_tokens), strictly separate from user. */
  avg_assistant_tokens: number;
  sample_size_user: number;
  sample_size_assistant: number;
  avatar_color: string | null;
  avatar_variant: number | null;
}

async function listChatbots(): Promise<ChatbotPickerOption[]> {
  const res = await api.get<{ success: boolean; chatbots: ChatbotPickerOption[] }>(
    '/cost-simulator/chatbots',
  );
  return res.data.chatbots;
}

async function getProfile(chatbotId: string): Promise<ChatbotProfile> {
  const res = await api.get<{ success: boolean; profile: ChatbotProfile }>(
    `/cost-simulator/chatbots/${chatbotId}/profile`,
  );
  return res.data.profile;
}

/**
 * Statistical agent profile built from measured llm_call_events. Drives
 * the "Base on real agent profile" path in the cost simulator — feeds
 * p10/p50/p90 distributions per metric instead of single means.
 */
export interface StatsBundle {
  mean: number;
  p50: number;
  p90: number;
  stddev: number;
  min: number;
  max: number;
}

export interface AgentProfileJson {
  sample_size: number;
  sample_messages: number;
  sample_events: number;
  days_with_activity: number;
  window_days: number;
  low_confidence: boolean;
  unattributed_events: number;
  messages_per_conversation: StatsBundle;
  embeddings_per_conversation: StatsBundle;
  embedding_tokens_per_conversation: StatsBundle;
  rag_rerank_per_conversation: StatsBundle;
  web_search_per_conversation: StatsBundle;
  title_calls_per_conversation: StatsBundle;
  summary_calls_per_conversation: StatsBundle;
  input_tokens_per_message: StatsBundle;
  output_tokens_per_message: StatsBundle;
  reasoning_tokens_per_message: StatsBundle;
  cache_read_tokens_per_message: StatsBundle;
  cache_creation_tokens_per_message: StatsBundle;
  iterations_per_message: StatsBundle;
  tool_calls_per_message: StatsBundle;
  cost_per_message_usd: StatsBundle;
  cache_hit_ratio: StatsBundle;
  tool_distribution: Record<string, { call_count: number; avg_tokens_per_call: number }>;
  model_distribution: Record<string, number>;
}

async function getAgentProfile(
  chatbotId: string,
  options: { windowDays?: number; fresh?: boolean } = {},
): Promise<AgentProfileJson | null> {
  const qs: string[] = [];
  if (options.windowDays) qs.push(`window_days=${options.windowDays}`);
  if (options.fresh) qs.push('fresh=1');
  const url = `/cost-simulator/chatbots/${chatbotId}/agent-profile${qs.length ? `?${qs.join('&')}` : ''}`;
  try {
    const res = await api.get<{ success: boolean; profile: AgentProfileJson }>(url);
    return res.data.profile;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Simulator p10/p50/p90 forecast from a real agent profile. */
export interface ProfileBandResult {
  sample_size: number;
  sample_messages: number;
  low_confidence: boolean;
  window_days: number;
  p10: any;
  p50: any;
  p90: any;
}

async function simulateFromAgentProfile(payload: {
  chatbot_id: string;
  window_days?: number;
  base_params: any;
}): Promise<ProfileBandResult> {
  const res = await api.post<{ success: boolean; result: ProfileBandResult }>(
    '/cost-simulator/simulate/from-agent-profile',
    payload,
  );
  return res.data.result;
}

// ─── Measured baseline (new simulator UX) ─────────────────────────────────

export interface MeasuredBaselineModelEntry {
  provider: string;
  model: string;
  event_count: number;
}

export interface MeasuredBaseline {
  conversation_count: number;
  avg_input_tokens: number;
  avg_output_tokens: number;
  avg_reasoning_tokens: number;
  avg_cache_read_tokens: number;
  avg_cache_creation_tokens: number;
  avg_total_cost_usd: number;
  /**
   * Per-component USD cost per conversation. These four sum to
   * avg_total_cost_usd and let the simulator show users *exactly* where
   * the money goes: $X to uncached input, $Y to output, $Z to cache read,
   * $W to cache write. Each is just `tokens × per-million rate ÷ 1M`.
   */
  avg_input_cost_usd: number;
  avg_output_cost_usd: number;
  avg_cache_read_cost_usd: number;
  avg_cache_write_cost_usd: number;
  cache_hit_ratio: number;
  p50_cost_usd: number;
  p90_cost_usd: number;
  avg_calls_per_conversation: number;
  /**
   * Avg user+assistant messages per conversation, computed from the
   * messages table for this chatbot+window. Lets the infrastructure
   * sim auto-fill "Avg msgs / conv" in measured mode.
   */
  avg_messages_per_conversation: number;
  /**
   * Avg total tokens per message (input+output+cache_read+cache_creation)
   * across the window. Auto-fills "Avg msg tokens" in measured mode.
   */
  avg_tokens_per_message: number;
}

export interface MeasuredBaselineResponse {
  success: boolean;
  chatbot_id: string;
  window_days: number;
  date_from: string;
  date_to: string;
  baseline_provider: string | null;
  baseline_model: string | null;
  model_distribution: MeasuredBaselineModelEntry[];
  baseline: MeasuredBaseline;
  /**
   * Per-conversation USD cost broken down by call_kind. Lets the simulator's
   * Feature toggles subtract precisely — turn off "Summaries" and we subtract
   * cost_per_conv_by_kind['summary'] from baseline.avg_total_cost_usd.
   */
  cost_per_conv_by_kind: Record<string, number>;
  alt_cost: {
    provider: string;
    model: string;
    avg_total_cost_usd: number;
    input_cost_usd: number;
    output_cost_usd: number;
    cache_read_cost_usd: number;
    cache_write_cost_usd: number;
    pricing_version: string;
  } | null;
  alt_cost_error?: string;
}

// ── Infrastructure projection (new) ─────────────────────────────────────

export interface InfrastructureRow {
  /**
   * Row identifier — matches the Advanced engine's camelCase keys
   * (dbStorage, dbCompute, gcsStorage, gcsOps, gcsEgress, computeBase,
   * computePerUser, computeConcurrency, redis). Lets the simulator and
   * the Advanced tab share i18n labels via the `label_i18n_key` below.
   */
  key: string;
  /** i18n key like `costSimulator.breakdown.dbStorage` — translate via `t()`. */
  label_i18n_key: string;
  cost: number;
  /**
   * Human-readable formula explanation — byte-identical to the
   * Advanced engine's `formula` string. Shows the math that produced
   * this row's cost.
   */
  detail: string;
}

export interface InfrastructureProjectionResponse {
  success: boolean;
  volume: {
    active_users: number;
    conversations: number;
    messages: number;
    file_chunks: number;
    summaries_per_month: number;
    peak_concurrent: number;
  };
  rows: InfrastructureRow[];
  total_infra_cost: number;
}

export interface InfrastructureProjectionParams {
  active_users: number;
  convs_per_user: number;
  num_tenants: number;
  msgs_per_conv: number;
  avg_msg_tokens: number;
  files_count: number;
  avg_file_mb: number;
  file_churn_pct: number;
  concurrent_pct: number;
}

async function getInfrastructureProjection(
  params: InfrastructureProjectionParams,
): Promise<InfrastructureProjectionResponse> {
  const res = await api.post<InfrastructureProjectionResponse>(
    '/cost-simulator/infrastructure-projection',
    params,
  );
  return res.data;
}

async function getMeasuredBaseline(params: {
  chatbotId: string;
  windowDays?: number;
  dateFrom?: string;
  dateTo?: string;
  targetModel?: string;
}): Promise<MeasuredBaselineResponse> {
  const qs: string[] = [`chatbot_id=${encodeURIComponent(params.chatbotId)}`];
  // Explicit range wins; window_days is a shortcut fallback.
  if (params.dateFrom) qs.push(`date_from=${encodeURIComponent(params.dateFrom)}`);
  if (params.dateTo) qs.push(`date_to=${encodeURIComponent(params.dateTo)}`);
  if (!params.dateFrom && !params.dateTo && params.windowDays) {
    qs.push(`window_days=${params.windowDays}`);
  }
  if (params.targetModel) qs.push(`target_model=${encodeURIComponent(params.targetModel)}`);
  const res = await api.get<MeasuredBaselineResponse>(
    `/cost-simulator/measured-baseline?${qs.join('&')}`,
  );
  return res.data;
}

// ─── Consolidated "simple" simulate endpoint ──────────────────────────────
// Single endpoint that takes multi-agent input + volume + features and
// returns the full breakdown the new Simulator tab needs in one round-trip.

export interface SimpleSimAgentInput {
  id: string;
  label: string;
  cost_per_conv_usd: number;
  model_label?: string;
  convs_per_user_per_month: number;
  files_count: number;
  avg_file_mb: number;
}

export interface SimpleSimRequest {
  agents: SimpleSimAgentInput[];
  active_users: number;
  num_tenants: number;
  summaries_enabled: boolean;
  insight_templates_count: number;
  file_churn_pct: number;
  concurrent_pct: number;
}

export interface SimpleSimAgentOut {
  id: string;
  label: string;
  cost_per_conv_usd: number;
  convs_per_month: number;
  ai_recurring: number;
  setup: {
    embedding: number;
    enrichment: number;
    bundle_context: number;
    total: number;
    detail: string;
  };
  recurring_files: {
    embedding: number;
    enrichment: number;
    bundle_context: number;
    total: number;
    detail: string;
  };
}

export interface SimpleSimPlatformFeatures {
  title: { cost: number; detail: string };
  summary: { cost: number; enabled: boolean; detail: string };
  insight_templates: { cost: number; count: number; detail: string };
}

export interface SimpleSimResponse {
  success: boolean;
  ai: {
    agents: SimpleSimAgentOut[];
    platform_features: SimpleSimPlatformFeatures;
    recurring_files_total: number;
    subtotal: number;
  };
  infra: {
    rows: InfrastructureRow[];
    subtotal: number;
  };
  setup: {
    subtotal: number;
  };
  monthly_recurring: number;
  first_month_total: number;
  volume: {
    active_users: number;
    num_tenants: number;
    total_convs: number;
    total_msgs: number;
    peak_concurrent: number;
    total_file_chunks: number;
  };
}

async function simpleSimulate(payload: SimpleSimRequest): Promise<SimpleSimResponse> {
  const res = await api.post<SimpleSimResponse>('/cost-simulator/simple-simulate', payload);
  return res.data;
}

export const chatbotPickerService = {
  listChatbots,
  getProfile,
  getAgentProfile,
  simulateFromAgentProfile,
  getMeasuredBaseline,
  getInfrastructureProjection,
  simpleSimulate,
};
